import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { DeleteIcon } from 'assets/images/icons';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { AgentStatus } from '@constants/agents';

const Card = styled('div')({
    padding: 16,
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 4,
});

const CardFooter = styled('div', {
    shouldForwardProp: prop => prop !== 'hasAccess',
})(({ hasAccess }) => ({
    paddingTop: 16,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& > div:nth-of-type(2)': {
        display: 'grid',
        gridGap: 6,
        gridTemplateColumns: `repeat(${hasAccess ? 3 : 1}, 32px)`,
        '@media (max-width: 700px)': {
            width: '100%',
            gridTemplateColumns: `${hasAccess ? '1fr 1fr 1fr' : '1fr'}`,
        },
    },
    '@media (max-width: 700px)': {
        flexDirection: 'column',
        alignItems: 'flex-start',
        '& div:nth-of-type(1)': {
            marginBottom: 16,
        },
    },
}));

const StyledIconButton = styled(IconButton)({
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 4,
    height: 32,
    padding: 2,
    '& svg': {
        width: 18,
        height: 18,
        '& path': {
            fill: 'rgba(0, 0, 0, 0.6)',
        },
    },
});

const TemplateItem = ({ data, companies, brands, isSuperAdmin, isSubmitting, onClickCard }) => {
    const { t } = useTranslation();

    const locationName = data.locationId
        ? companies?.find(company => company.companyId === data.locationId)?.internalName
        : brands?.find(brand => brand.id === data.brandId)?.title;

    const StatusMap = {
        [AgentStatus.Active]: t('common.active'),
        [AgentStatus.Disabled]: t('common.disabled'),
    };

    return (
        <Card>
            <Typography variant="subtitle2">{data.name}</Typography>
            <Typography
                variant="caption"
                sx={{ margin: '4px 0 16px', display: 'inline-block', color: 'rgba(0, 0, 0, 0.6)' }}
            >
                {locationName}
            </Typography>
            <div style={{ width: '100%', height: 1, backgroundColor: 'rgba(0, 0, 0, 0.12)' }} />
            <CardFooter hasAccess={isSuperAdmin}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                        <span
                            style={{
                                ...(data.status === AgentStatus.Active && { backgroundColor: '#4CC474' }),
                                ...(data.status === AgentStatus.Disabled && { backgroundColor: '#FF5853' }),
                                display: 'inline-block',
                                height: 8,
                                width: 8,
                                borderRadius: '50%',
                                marginRight: 8,
                            }}
                        />
                        <span>{StatusMap[data.status]}</span>
                    </Typography>
                </div>
                <div>
                    {isSuperAdmin ? (
                        <>
                            <StyledIconButton disabled={isSubmitting} onClick={() => onClickCard(data, 'toggleStatus')}>
                                {data.status === AgentStatus.Active ? <PauseIcon /> : <PlayArrowIcon />}
                            </StyledIconButton>
                            <StyledIconButton disabled={isSubmitting} onClick={() => onClickCard(data, 'edit')}>
                                <EditIcon />
                            </StyledIconButton>
                            <StyledIconButton disabled={isSubmitting} onClick={() => onClickCard(data, 'delete')}>
                                <DeleteIcon />
                            </StyledIconButton>
                        </>
                    ) : (
                        <StyledIconButton disabled={isSubmitting} onClick={() => onClickCard(data, 'view')}>
                            <VisibilityIcon />
                        </StyledIconButton>
                    )}
                </div>
            </CardFooter>
        </Card>
    );
};

export default TemplateItem;
